import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../i18n";

const HOTELS = {
  "premium-inn": {
    nameMk: "Премиум Инн",
    nameEn: "Premium Inn",
    subKey: "hotel.sub",
    phone: "38978457878",
    rooms: ["deluxeApartment", "deluxeFamilyRoom", "familySuite", "quadrupleRoom", "tripleRoom", "doubleRoom"],
    images: {
      deluxeApartment: ["/images/premium-inn/deluxe-1.webp", "/images/premium-inn/deluxe-2.webp", "/images/premium-inn/deluxe-3.webp", "/images/premium-inn/deluxe-4.webp", "/images/premium-inn/deluxe-5.webp", "/images/premium-inn/deluxe-6.webp", "/images/premium-inn/deluxe-7.webp", "/images/premium-inn/deluxe-8.webp"],
      deluxeFamilyRoom: ["/images/premium-inn/family-1.webp", "/images/premium-inn/family-2.webp", "/images/premium-inn/family-3.webp", "/images/premium-inn/family-4.webp"],
      familySuite: ["/images/premium-inn/suite-1.webp", "/images/premium-inn/suite-2.webp", "/images/premium-inn/suite-3.webp", "/images/premium-inn/suite-4.webp"],
      quadrupleRoom: ["/images/premium-inn/quad-1.webp", "/images/premium-inn/quad-2.webp", "/images/premium-inn/quad-3.webp", "/images/premium-inn/quad-4.webp"],
      tripleRoom: ["/images/premium-inn/triple-1.webp", "/images/premium-inn/triple-2.webp", "/images/premium-inn/triple-3.webp", "/images/premium-inn/triple-4.webp"],
      doubleRoom: ["/images/premium-inn/double-1.webp", "/images/premium-inn/double-2.webp", "/images/premium-inn/double-3.webp"],
    },
    prices: {
      deluxeApartment: 40,
      deluxeFamilyRoom: 40,
      familySuite: 40,
      quadrupleRoom: 35,
      tripleRoom: 30,
      doubleRoom: 25,
    },
  },
  feniks: {
    nameMk: "Феникс",
    nameEn: "Fenix",
    subKey: "hotel.subFeniks",
    phone: "38978321872",
    rooms: ["doubleRoom", "tripleRoom", "quadrupleRoom", "commonAreas"],
    images: {
      doubleRoom: ["/images/feniks/double-1.webp", "/images/feniks/double-2.webp", "/images/feniks/double-3.webp", "/images/feniks/double-4.webp"],
      tripleRoom: ["/images/feniks/triple-1.webp", "/images/feniks/triple-2.webp", "/images/feniks/triple-3.webp", "/images/feniks/triple-4.webp", "/images/feniks/triple-5.webp", "/images/feniks/triple-6.webp", "/images/feniks/triple-7.webp"],
      quadrupleRoom: ["/images/feniks/quad-1.webp", "/images/feniks/quad-2.webp", "/images/feniks/quad-3.webp", "/images/feniks/quad-4.webp"],
      commonAreas: ["/images/feniks/common-1.webp", "/images/feniks/common-2.webp", "/images/feniks/common-3.webp", "/images/feniks/common-4.webp", "/images/feniks/common-5.webp"],
    },
    prices: {
      doubleRoom: 25,
      tripleRoom: 30,
      quadrupleRoom: 35,
    },
  },
};

export default function HotelPage({ slug }) {
  const { lang, t } = useLang();
  const hotel = HOTELS[slug];
  const [active, setActive] = useState(0);
  const [activeImg, setActiveImg] = useState(0);
  const roomKey = hotel.rooms[active];
  const roomName = t(`hotel.rooms.${roomKey}`);
  const roomImages = hotel.images[roomKey] || [];
  const roomPrice = hotel.prices[roomKey];
  const hotelName = lang === "mk" ? hotel.nameMk : hotel.nameEn;
  const waMsg = t("hotel.waRoom").replace("{room}", roomName).replace("{hotel}", hotelName);

  return (
    <main data-testid="hotel-page" className="pt-20 md:pt-24 pb-24 bg-sand min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Link to="/" data-testid="back-to-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ink/50 hover:text-terra transition-colors mb-10">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {t("detail.back")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10 md:mb-14"
        >
          <p className="text-terra text-xs font-bold tracking-[0.35em] uppercase mb-4">{t("nav.hotels")}</p>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-none mb-4" data-testid="hotel-title">
            {hotelName}
          </h1>
          <p className="text-ink/60 text-sm md:text-base max-w-xl leading-relaxed">{t(hotel.subKey)}</p>
        </motion.div>

        <div className="flex flex-wrap gap-3 mb-10 md:mb-14" data-testid="hotel-tabs">
          {hotel.rooms.map((key, i) => (
            <button
              key={key}
              data-testid={`hotel-tab-${key}`}
              onClick={() => {
                setActive(i);
                setActiveImg(0);
              }}
              className={`px-5 py-2.5 text-xs font-bold tracking-[0.15em] uppercase border transition-colors duration-300 ${
                i === active ? "bg-terra text-sand border-terra" : "border-ink/25 text-ink/70 hover:border-terra hover:text-terra"
              }`}
            >
              {t(`hotel.rooms.${key}`)}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hotel-room-panel"
            className="border border-ink/10 bg-coal text-sand p-8 md:p-14"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
              <div className="max-w-xl">
                {roomPrice ? (
                  <p className="font-serif text-2xl md:text-3xl mb-4" data-testid="hotel-room-price">
                    <span className="text-xs font-sans text-sand/50 mr-1 uppercase tracking-widest">{t("grid.from")}</span>€{roomPrice}
                    <span className="text-xs font-sans text-sand/50">{t("grid.perNight")}</span>
                  </p>
                ) : (
                  <p className="text-ember text-xs font-bold tracking-[0.35em] uppercase mb-4">{t("grid.comingSoon")}</p>
                )}
                <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-none mb-4" data-testid="hotel-room-name">{roomName}</h2>
                <p className="text-sand/60 text-sm md:text-base leading-relaxed">{t(roomPrice ? "hotel.roomInfo" : "hotel.roomSub")}</p>
              </div>
              <div className="flex flex-wrap gap-4 shrink-0">
                <div className="flex flex-col items-center gap-2">
                  <a
                    href={`https://wa.me/${hotel.phone}?text=${encodeURIComponent(waMsg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hotel-whatsapp-button"
                    className="inline-flex items-center gap-3 px-7 py-3.5 text-xs font-bold tracking-[0.2em] uppercase transition-transform duration-300 hover:scale-[1.03]"
                    style={{ backgroundColor: "#25D366", color: "#0b1f13" }}
                  >
                    <img src="https://cdn.simpleicons.org/whatsapp/0b1f13" alt="WhatsApp" className="w-5 h-5" /> WhatsApp
                  </a>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <a
                    href={`viber://chat?number=%2B${hotel.phone}`}
                    data-testid="hotel-viber-button"
                    className="inline-flex items-center gap-3 px-7 py-3.5 text-xs font-bold tracking-[0.2em] uppercase text-white transition-transform duration-300 hover:scale-[1.03]"
                    style={{ backgroundColor: "#7360F2" }}
                  >
                    <img src="https://cdn.simpleicons.org/viber/ffffff" alt="Viber" className="w-5 h-5" /> Viber
                  </a>
                </div>
              </div>
            </div>
            {roomImages.length > 0 && (
              <div className="mt-10 md:mt-14">
                <div className="spotlight-frame aspect-[16/10] md:aspect-[16/8] border border-sand/10 mb-3">
                  <img src={roomImages[activeImg]} alt={roomName} data-testid="hotel-main-image" className="w-full h-full object-cover" />
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: "thin" }} data-testid="hotel-thumbs">
                  {roomImages.map((img, i) => (
                    <button
                      key={img}
                      data-testid={`hotel-thumb-${i}`}
                      onClick={() => setActiveImg(i)}
                      className={`spotlight-frame shrink-0 w-32 md:w-44 aspect-[16/10] border transition-colors duration-300 ${activeImg === i ? "border-ember" : "border-sand/20 opacity-70 hover:opacity-100"}`}
                    >
                      <img src={img} alt={`${roomName} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </main>
  );
}
